import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  FileTextOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  DatabaseOutlined,
  TranslationOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchSqlData } from '../../services/api';

const { Title, Text } = Typography;

/** Generic tam tablo çekici — fetchSqlData tüm satırları döner (küçük tablolar). */
const fetchTable = async (table: string): Promise<any[]> => {
  const data = await fetchSqlData(table);
  return Array.isArray(data) ? data : data?.data || [];
};

/** mhidocs status kodları için deterministik hex paleti (anlamsal değil, sadece ayırt edici). */
const STATUS_COLORS = ['#1677ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96', '#13c2c2', '#fa541c', '#2f54eb'];
const colorForStatus = (_code: string, index: number) => STATUS_COLORS[index % STATUS_COLORS.length];

interface MonthSeries {
  label: string;
  a: number; // birincil seri (gelen / transmittal)
  b?: number; // ikincil seri (giden)
}

/** Bağımlılıksız dikey kolon grafiği (1 veya 2 seri). */
const ColumnChart: React.FC<{
  data: MonthSeries[];
  aName: string;
  bName?: string;
  aColor?: string;
  bColor?: string;
  height?: number;
}> = ({ data, aName, bName, aColor = '#1677ff', bColor = '#52c41a', height = 150 }) => {
  const max = Math.max(1, ...data.map((d) => Math.max(d.a, d.b ?? 0)));
  if (!data.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, width: '100%' }}>
        {data.map((d) => (
          <div
            key={d.label}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: 2,
                height,
                width: '100%',
              }}
            >
              <Tooltip title={`${aName}: ${d.a}`}>
                <div
                  style={{
                    width: bName ? '42%' : '70%',
                    height: `${(d.a / max) * 100}%`,
                    minHeight: d.a > 0 ? 2 : 0,
                    background: aColor,
                    borderRadius: '2px 2px 0 0',
                  }}
                />
              </Tooltip>
              {bName && (
                <Tooltip title={`${bName}: ${d.b ?? 0}`}>
                  <div
                    style={{
                      width: '42%',
                      height: `${((d.b ?? 0) / max) * 100}%`,
                      minHeight: (d.b ?? 0) > 0 ? 2 : 0,
                      background: bColor,
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: 11, marginTop: 4, whiteSpace: 'nowrap' }}>
              {d.label}
            </Text>
          </div>
        ))}
      </div>
      <Space size="large" style={{ marginTop: 8 }}>
        <Space size={4}>
          <span style={{ width: 10, height: 10, background: aColor, display: 'inline-block', borderRadius: 2 }} />
          <Text type="secondary" style={{ fontSize: 12 }}>{aName}</Text>
        </Space>
        {bName && (
          <Space size={4}>
            <span style={{ width: 10, height: 10, background: bColor, display: 'inline-block', borderRadius: 2 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{bName}</Text>
          </Space>
        )}
      </Space>
    </div>
  );
};

/** Son N ayı 'YYYY-MM' etiketleriyle üret. */
const lastMonths = (n: number): string[] => {
  const out: string[] = [];
  const now = dayjs();
  for (let i = n - 1; i >= 0; i -= 1) {
    out.push(now.subtract(i, 'month').format('YYYY-MM'));
  }
  return out;
};

const DccDashboard: React.FC = () => {
  const correspondencesQ = useQuery({
    queryKey: ['dcc_dashboard', 'correspondences'],
    queryFn: () => fetchTable('dcc_indextable_correspondences'),
    staleTime: 60_000,
  });
  const mhidocsQ = useQuery({
    queryKey: ['dcc_dashboard', 'mhidocs'],
    queryFn: () => fetchTable('dcc_indextable_mhidocs'),
    staleTime: 60_000,
  });
  const translationQ = useQuery({
    queryKey: ['dcc_dashboard', 'translation'],
    queryFn: () => fetchTable('dcc_translation_log'),
    staleTime: 60_000,
  });
  const rootsQ = useQuery({
    queryKey: ['dcc_dashboard', 'roots'],
    queryFn: () => fetchTable('dcc_search_index_roots'),
    staleTime: 60_000,
  });

  const loading =
    correspondencesQ.isLoading || mhidocsQ.isLoading || translationQ.isLoading || rootsQ.isLoading;
  const isFetching =
    correspondencesQ.isFetching || mhidocsQ.isFetching || translationQ.isFetching || rootsQ.isFetching;
  const anyError =
    correspondencesQ.isError || mhidocsQ.isError || translationQ.isError || rootsQ.isError;

  const refreshAll = () => {
    correspondencesQ.refetch();
    mhidocsQ.refetch();
    translationQ.refetch();
    rootsQ.refetch();
  };

  const corr = correspondencesQ.data || [];
  const docs = mhidocsQ.data || [];
  const trans = translationQ.data || [];
  const roots = rootsQ.data || [];

  // ---- Yazışma metrikleri ----
  const corrStats = useMemo(() => {
    const incoming = corr.filter((r) => String(r.type || '').toLowerCase() === 'incoming').length;
    const outgoing = corr.filter((r) => String(r.type || '').toLowerCase() === 'outgoing').length;
    return { total: corr.length, incoming, outgoing };
  }, [corr]);

  const corrMonthly = useMemo<MonthSeries[]>(() => {
    const months = lastMonths(12);
    const idx: Record<string, MonthSeries> = {};
    months.forEach((m) => {
      idx[m] = { label: dayjs(m + '-01').format('MMM YY'), a: 0, b: 0 };
    });
    corr.forEach((r) => {
      if (!r.date) return;
      const m = dayjs(r.date).format('YYYY-MM');
      if (!idx[m]) return;
      if (String(r.type || '').toLowerCase() === 'incoming') idx[m].a += 1;
      else idx[m].b = (idx[m].b ?? 0) + 1;
    });
    return months.map((m) => idx[m]);
  }, [corr]);

  const recentLetters = useMemo(() => {
    return [...corr]
      .filter((r) => r.date)
      .sort((x, y) => dayjs(y.date).valueOf() - dayjs(x.date).valueOf())
      .slice(0, 8);
  }, [corr]);

  // ---- Doküman metrikleri ----
  const docStats = useMemo(() => {
    const distinct = new Set(docs.map((d) => String(d.document_no || '')).filter(Boolean));
    return { total: docs.length, distinct: distinct.size };
  }, [docs]);

  const statusDist = useMemo(() => {
    const map: Record<string, number> = {};
    docs.forEach((d) => {
      const s = String(d.status || '').trim() || '—';
      map[s] = (map[s] || 0) + 1;
    });
    const total = docs.length || 1;
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([code, count], i) => ({ code, count, pct: (count / total) * 100, color: colorForStatus(code, i) }));
  }, [docs]);

  const docMonthly = useMemo<MonthSeries[]>(() => {
    const months = lastMonths(8);
    const idx: Record<string, MonthSeries> = {};
    months.forEach((m) => {
      idx[m] = { label: dayjs(m + '-01').format('MMM YY'), a: 0 };
    });
    docs.forEach((d) => {
      if (!d.transmittal_date) return;
      const m = dayjs(d.transmittal_date).format('YYYY-MM');
      if (idx[m]) idx[m].a += 1;
    });
    return months.map((m) => idx[m]);
  }, [docs]);

  const mostRevised = useMemo(() => {
    const map: Record<string, { document_no: string; revisions: number; title: string; last: string }> = {};
    docs.forEach((d) => {
      const key = String(d.document_no || '');
      if (!key) return;
      if (!map[key]) map[key] = { document_no: key, revisions: 0, title: String(d.title || ''), last: '' };
      map[key].revisions += 1;
      const td = d.transmittal_date ? dayjs(d.transmittal_date).format('YYYY-MM-DD') : '';
      if (td && td > map[key].last) map[key].last = td;
    });
    return Object.values(map)
      .filter((r) => r.revisions > 1)
      .sort((a, b) => b.revisions - a.revisions)
      .slice(0, 8);
  }, [docs]);

  // ---- Çeviri metrikleri ----
  const transStats = useMemo(() => {
    const by: Record<string, number> = {};
    trans.forEach((t) => {
      const s = String(t.status || '').toLowerCase();
      by[s] = (by[s] || 0) + 1;
    });
    const pending = (by['queued'] || 0) + (by['sent'] || 0);
    const completed = by['completed'] || 0;
    return { pending, completed, queued: by['queued'] || 0, sent: by['sent'] || 0, total: trans.length };
  }, [trans]);

  // ---- İndeks sağlığı ----
  const rootStats = useMemo(() => {
    const now = dayjs();
    let stale = 0;
    let errored = 0;
    roots.forEach((r) => {
      if (r.last_error) errored += 1;
      if (!r.last_refresh_at || now.diff(dayjs(r.last_refresh_at), 'day') > 7) stale += 1;
    });
    return { total: roots.length, stale, errored, ok: roots.length - stale - (errored > stale ? errored - stale : 0) };
  }, [roots]);

  const openLetter = (record: any) => {
    const filePath = String(record?.letter_path || record?.file_path || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '');
    if (!filePath) return;
    const parentPath = filePath.includes('/') ? filePath.slice(0, filePath.lastIndexOf('/')) : '';
    if (!parentPath) return;
    const qs = new URLSearchParams();
    qs.set('path', parentPath);
    window.open(`/dcc/documents?${qs.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" wrap gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 0 }}>
            DCC Dashboard
          </Title>
          <Text type="secondary">Document control, correspondence, translation, and index status</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={isFetching}>
            Refresh
          </Button>
        </Col>
      </Row>

      {anyError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Some data could not be loaded"
          description="The dashboard may be showing partial data. Try Refresh or check the backend connection."
        />
      )}

      <Spin spinning={loading}>
        {/* KPI şeridi */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Documents (mhidocs)"
                value={docStats.total}
                prefix={<FileTextOutlined />}
              />
              <Text type="secondary">{docStats.distinct} unique documents</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Correspondence" value={corrStats.total} prefix={<FileTextOutlined />} />
              <Space size="small">
                <Tag color="blue" icon={<ArrowDownOutlined />}>{corrStats.incoming} incoming</Tag>
                <Tag color="green" icon={<ArrowUpOutlined />}>{corrStats.outgoing} outgoing</Tag>
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Translation (pending)"
                value={transStats.pending}
                prefix={<TranslationOutlined />}
              />
              <Text type="secondary">{transStats.completed} completed</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Index roots"
                value={rootStats.total - rootStats.stale}
                suffix={`/ ${rootStats.total}`}
                prefix={<DatabaseOutlined />}
                valueStyle={rootStats.errored > 0 ? { color: '#cf1322' } : undefined}
              />
              <Text type={rootStats.errored > 0 ? 'danger' : 'secondary'}>
                {rootStats.errored > 0
                  ? `${rootStats.errored} errors`
                  : rootStats.stale > 0
                    ? `${rootStats.stale} stale (>7d)`
                    : 'all up to date'}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Operasyonel + yönetsel: mhidocs */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Document Status Distribution" size="small">
              {statusDist.length ? (
                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                  {statusDist.map((s) => (
                    <div key={s.code}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Tag color={s.color} style={{ marginRight: 0 }}>{s.code}</Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {s.count} ({s.pct.toFixed(0)}%)
                        </Text>
                      </div>
                      <div style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 3, height: 8 }}>
                        <div
                          style={{
                            width: `${s.pct}%`,
                            height: 8,
                            background: s.color,
                            borderRadius: 3,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Codes come from the source system (FR/FA/FI…); a status dictionary can be defined for descriptions.
                  </Text>
                </Space>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Monthly Transmittal Volume (last 8 months)" size="small">
              <ColumnChart data={docMonthly} aName="Transmittal" aColor="#722ed1" />
            </Card>
          </Col>
        </Row>

        {/* Yönetsel */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Most Revised Documents" size="small">
              <Table
                size="small"
                rowKey="document_no"
                dataSource={mostRevised}
                pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> }}
                columns={[
                  { title: 'Document No', dataIndex: 'document_no', key: 'document_no', width: 170, ellipsis: true },
                  {
                    title: 'Title',
                    dataIndex: 'title',
                    key: 'title',
                    ellipsis: true,
                    render: (v: string) => <Tooltip title={v}>{v}</Tooltip>,
                  },
                  {
                    title: 'Rev.',
                    dataIndex: 'revisions',
                    key: 'revisions',
                    width: 70,
                    align: 'center',
                    render: (v: number) => <Tag color="volcano">{v}</Tag>,
                  },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Monthly Correspondence Volume (last 12 months)" size="small">
              <ColumnChart data={corrMonthly} aName="Incoming" bName="Outgoing" aColor="#1677ff" bColor="#52c41a" />
            </Card>
          </Col>
        </Row>

        {/* Operasyonel liste + sistem */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Recent Correspondence" size="small">
              <Table
                size="small"
                rowKey={(r: any) => String(r.id)}
                dataSource={recentLetters}
                pagination={false}
                onRow={(record: any) => ({ onClick: () => openLetter(record), style: { cursor: 'pointer' } })}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    width: 100,
                    render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type',
                    width: 90,
                    render: (v: string) => {
                      const s = String(v || '');
                      if (!s) return '';
                      return <Tag color={s.toLowerCase() === 'incoming' ? 'blue' : 'green'}>{s}</Tag>;
                    },
                  },
                  { title: 'Ref #', dataIndex: 'ref_num', key: 'ref_num', width: 130, ellipsis: true },
                  {
                    title: 'Subject',
                    dataIndex: 'subject',
                    key: 'subject',
                    ellipsis: true,
                    render: (v: string) => <Tooltip title={v}>{v}</Tooltip>,
                  },
                ]}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="System: Index Roots & Translation"
              size="small"
              extra={
                <Space size={4}>
                  <Tag color="processing">{transStats.queued} queued</Tag>
                  <Tag color="warning">{transStats.sent} sent</Tag>
                  <Tag color="success">{transStats.completed} done</Tag>
                </Space>
              }
            >
              <Table
                size="small"
                rowKey="root"
                dataSource={roots}
                pagination={false}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> }}
                columns={[
                  { title: 'Root', dataIndex: 'root', key: 'root', width: 150, ellipsis: true },
                  {
                    title: 'Last Refresh',
                    dataIndex: 'last_refresh_at',
                    key: 'last_refresh_at',
                    width: 150,
                    render: (v: string) => {
                      if (!v) return <Tag color="default">never</Tag>;
                      const stale = dayjs().diff(dayjs(v), 'day') > 7;
                      return (
                        <Text type={stale ? 'warning' : undefined}>
                          {dayjs(v).format('YYYY-MM-DD HH:mm')}
                        </Text>
                      );
                    },
                  },
                  {
                    title: 'Records',
                    dataIndex: 'last_refresh_count',
                    key: 'last_refresh_count',
                    width: 80,
                    align: 'right',
                    render: (v: number) => (typeof v === 'number' ? v : '—'),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'last_error',
                    key: 'last_error',
                    render: (v: string) =>
                      v ? (
                        <Tooltip title={v}>
                          <Tag color="error">error</Tag>
                        </Tooltip>
                      ) : (
                        <Tag color="success">ok</Tag>
                      ),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default DccDashboard;
